/**
 * The three ways somebody puts this site inside their own.
 *
 * An embed is a share that keeps working. A post about an outage is worth one click on the day it
 * is written; a badge in a README or a strip on a team dashboard is a permanent, live link that
 * lights up on its own the next time a provider falls over — which is the only kind of backlink
 * this site can earn by being useful rather than by being funny.
 *
 * Two shapes, because they fail differently and README hosts are strict:
 *
 *   - **`/embed?p=`** is a live iframe: the real client, the real socket, counters that move.
 *     Everything that can run an iframe gets this.
 *   - **`/badge.svg?provider=`** is one static image (B15). GitHub and npm strip iframes and
 *     scripts out of rendered Markdown, so the *only* thing that can appear in a README is an
 *     image — which is why two of the three snippets below are the badge and only one is the
 *     iframe.
 *
 * Every snippet is built from an {@link EmbedSubject}, and the reason it is an object rather than
 * one provider id is F33: the same three snippets are offered from the share sheet on the home page
 * *and* from the block at the foot of every status page, and those two want different links. A
 * badge pasted from a status page has to point back at that page with the phrase somebody searched
 * for as its anchor text — `[OpenAI status](…/status/openai)`, not `[Must Have AI status](…/)` —
 * because anchor text is the strongest thing one page can say about another and a link that says
 * nothing is a link that ranks nothing.
 *
 * Kept as pure string builders in `src/share` rather than inside the component so the exact
 * markup a visitor pastes into somebody else's page is covered by a unit test. Attribute order,
 * the `title` on the iframe and the `alt` on the image are all part of the contract B15's
 * hand-off note asks to keep stable, and a component test asserting rendered output would not
 * catch a change to any of them.
 */

import type { ProviderId } from '@/net/transport'
import { SEO_SITE_NAME } from '@/seo/copy'
import { providerLabel } from './shareText'

/** The three squares and the whole town: what the share sheet on the home page is ever about. */
export type EmbedTarget = ProviderId | 'all'

export type SnippetFormat = 'iframe' | 'markdown' | 'html'

/** What a set of snippets is *about*, and where it points. */
export interface EmbedSubject {
  /**
   * The `/badge.svg?provider=` value: `all`, a provider id, or one of the four product slugs the
   * badge learned in F33 (`docs/api.md` § 2).
   */
  badge: string
  /**
   * The `/embed?p=` value. Deliberately narrower than {@link badge}: the live strip has no product
   * variant, so a product's iframe shows its parent's square and says so through its title.
   */
  frame: EmbedTarget
  /** The page the link points at, site-relative. */
  page: string
  /** The anchor text and the image's `alt` — the phrase somebody would search for. */
  phrase: string
}

export interface Snippet {
  format: SnippetFormat
  /** The tab label. */
  label: string
  /** One line saying where this one works, because the answer differs per host. */
  hint: string
  code: string
}

/** The default box. 320 × 64 is three pills and a counter at a readable size (F20 § 2). */
export const EMBED_WIDTH = 320
export const EMBED_HEIGHT = 64

/**
 * The badge's own height, on the `<img>` in the HTML snippet.
 *
 * It is what stops the image reflowing the line it was pasted into while the SVG is fetched, on a
 * page whose author has no idea how tall our badge is. The SVG's own `height` attribute is the same
 * 20 (`BadgeEndpoint.Height`).
 */
export const BADGE_HEIGHT = 20

/**
 * The alt/title text of the share sheet's own snippets, unchanged since B15.
 *
 * Its hand-off note asks that this wording stay put, so a visitor who swaps one snippet for another
 * does not change what a screen reader says about their page. The status pages pass their own
 * phrase instead — a different surface with a different job, not a change to this one.
 */
function altText(target: EmbedTarget): string {
  return target === 'all' ? `${SEO_SITE_NAME} status` : `${SEO_SITE_NAME} status — ${providerLabel(target)}`
}

/** What the share sheet embeds: the square it was opened from, linking to the home page. */
export function siteSubject(target: EmbedTarget): EmbedSubject {
  return { badge: target, frame: target, page: '/', phrase: altText(target) }
}

/**
 * The Markdown link's hover title.
 *
 * The site's name once, never twice: the status pages' phrase is "OpenAI status" and wants the
 * brand appended, while the share sheet's already carries it.
 */
function linkTitle(phrase: string): string {
  return phrase.includes(SEO_SITE_NAME) ? phrase : `${phrase} — ${SEO_SITE_NAME}`
}

export function embedSnippets(subject: EmbedSubject, origin: string): readonly Snippet[] {
  const base = origin.replace(/\/+$/, '')
  const { badge, frame, phrase } = subject
  const href = base + (subject.page === '/' ? '' : subject.page)
  const image = `${base}/badge.svg?provider=${badge}`

  return [
    {
      format: 'iframe',
      label: 'iframe',
      hint: 'Live, updates on its own. Works anywhere that allows an iframe.',
      code:
        `<iframe src="${base}/embed?p=${frame}" width="${EMBED_WIDTH}" height="${EMBED_HEIGHT}"` +
        ` style="border:0" title="${phrase}"></iframe>`,
    },
    {
      format: 'markdown',
      label: 'Markdown',
      hint: 'For a README. GitHub strips iframes, so this one is the badge image.',
      code: `[![${phrase}](${image})](${href} "${linkTitle(phrase)}")`,
    },
    {
      format: 'html',
      label: 'HTML image',
      hint: 'The same badge for a page that takes HTML but not frames.',
      code: `<a href="${href}"><img alt="${phrase}" src="${image}" height="${BADGE_HEIGHT}"></a>`,
    },
  ]
}
